import React from 'react';
import ReactDOM from 'react-dom/client';
import { i18nReady } from '@maat/store-i18n';
import { AppContext, ErrorLevel, takeBootRecovery } from '@maat/store-error';
import { isStaleChunkError, reloadForStaleChunk } from '@maat/components-errors';
import { setAppBuildGetter, setReloadRequiredHandler } from '@maat/api-base';
import { buildInfo } from './buildInfo';
import { installBfcacheVersionCheck } from './bfcacheVersionCheck';
import { reloadRequiredStore } from './reloadRequiredStore';
import { setExecutingEntryUrl } from './executingEntry';
import { App } from './App.tsx';

declare global {
    interface Window {
        /** Set the moment the entry module executes — read by the inline
         * pre-boot watchdog in index.html to tell "bundle never loaded"
         * (its problem) apart from anything post-boot (the in-app handlers'). */
        __maatBooted?: boolean;
    }
}
// FIRST statement on purpose: executing at all proves the module graph loaded.
window.__maatBooted = true;

// `import.meta.url` must be evaluated in THIS module: main.tsx is guaranteed to
// live in a chunk the MF entry bootstrap imports directly, so its URL is
// comparable against version.json's `entryChunks`. Evaluated anywhere else,
// Rollup may hoist it into a shared chunk and the check false-positives. See
// src/executingEntry.ts.
setExecutingEntryUrl(import.meta.url);

// Boot succeeded — drop the pre-boot watchdog's cache-buster (?cb=<ts>) so it
// never sticks in the address bar or a shared link. Cosmetic: the SPA router
// keys off pathname.
if (window.location.search.includes('cb=')) {
    const params = new URLSearchParams(window.location.search);
    if (params.has('cb')) {
        params.delete('cb');
        const rest = params.toString();
        window.history.replaceState(
            window.history.state,
            '',
            window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash,
        );
    }
}

// Version handshake: report this build on every API request, and raise a
// non-blocking toast when the backend says the client is below the minimum.
setAppBuildGetter(() => buildInfo.buildNumber);
setReloadRequiredHandler(() => reloadRequiredStore.trigger());

// Self-heal across deploys and failed downloads. Lazy components recover through
// lazyWithRetry; this catches the dynamic imports nobody awaits.
// NEVER preventDefault() Vite's `vite:preloadError`: Vite then RESOLVES the failed
// import as `undefined` instead of rejecting, and React.lazy crashes on
// `undefined.default` (Sentry GUEST-R / GUEST-16) where no recovery can see it.
window.addEventListener('unhandledrejection', (event) => {
    if (isStaleChunkError(event.reason)) {
        reloadForStaleChunk('unhandled-import', String(event.reason?.message ?? event.reason));
    }
});
// Third self-heal path: a bfcache restore resumes an old build with zero network
// activity, so re-check the deployed version and reload if it moved.
installBfcacheVersionCheck();

// The shell is the Module Federation HOST, so this is the ONE Sentry init in the
// federated build; the reporter reaches every remote via the @maat/store-error
// singleton, and no DSN leaves reporting off. The SDK is imported at idle rather
// than in the eager entry chunk (the FCP win) — pre-init global errors escape
// reporting, which is the accepted trade-off; the handlers above stay eager
// because they do not depend on Sentry.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
    const initReporting = async () => {
        const { initSentry, setupGlobalHandlers } = await import('@maat/store-sentry');
        // Environment is auto-detected; Local returns null unless
        // VITE_SENTRY_FORCE is set, so nothing is sent from localhost.
        const reporter = initSentry({
            dsn: sentryDsn,
            app: AppContext.Shell,
            // The git SHA from index.html's meta, falling back to VITE_RELEASE.
            release: buildInfo.sha ?? import.meta.env.VITE_RELEASE,
            // Tags every event so exceptions group by the deployment they came from.
            buildContext: {
                sha: buildInfo.sha,
                version: buildInfo.version,
                buildNumber: buildInfo.buildNumber,
            },
            force: !!import.meta.env.VITE_SENTRY_FORCE,
        });
        if (reporter) {
            setupGlobalHandlers();
            const recovery = takeBootRecovery();
            if (recovery) {
                reporter.reportMessage('boot recovered after a reload', ErrorLevel.Warning, {
                    extra: { ...recovery },
                    fingerprint: ['boot-recovery', recovery.reason],
                });
            }
        }
    };
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => void initReporting());
    } else {
        setTimeout(() => void initReporting(), 0);
    }
}

// The non-English catalogs are lazy; hold the first render until the cookie's
// language is in so no key ever flashes (resolves immediately for English).
void i18nReady.then(() => {
    ReactDOM.createRoot(document.getElementById('root')!).render(
        <React.StrictMode>
            <App />
        </React.StrictMode>,
    );
});
